import { Modality, Valence, useGetTeamsQuery } from '../../../api/client'
import { MODALITY_OPTIONS } from '../../../models/Modality'
import { VALENCE_OPTIONS } from '../../../models/Valence'
import AccountFilter from '../../../components/content/search/AccountFilter'

/** Archived is a boolean on the server; "all" is the absence of the filter. */
export type ClassifierStatusFilter = 'active' | 'archived' | 'all'

const CONTROL =
    'rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-700 placeholder-ink-400 focus:border-arwen-500 focus:outline-none focus:ring-1 focus:ring-arwen-500'

type Props = {
    /** The raw search box value — debouncing before it hits the server is the page's job. */
    search: string
    onSearchChange: (value: string) => void
    modality?: Modality
    onModalityChange: (value?: Modality) => void
    valence?: Valence
    onValenceChange: (value?: Valence) => void
    status: ClassifierStatusFilter
    onStatusChange: (value: ClassifierStatusFilter) => void
    /** Narrow to classifiers deployed to any profile in this team. — Claude */
    teamId?: number
    onTeamChange: (value?: number) => void
    /** Narrow to classifiers deployed to any of these profiles. — Claude */
    accountIds: number[]
    onAccountIdsChange: (value: number[]) => void
}

export default function ClassifierLibraryFilters({
    search,
    onSearchChange,
    modality,
    onModalityChange,
    valence,
    onValenceChange,
    status,
    onStatusChange,
    teamId,
    onTeamChange,
    accountIds,
    onAccountIdsChange
}: Props) {
    const { data: teamsData } = useGetTeamsQuery()
    const teams = [...(teamsData?.getTeams ?? [])].sort((a, b) => a.name.localeCompare(b.name))

    return (
        <div className="flex flex-wrap items-center gap-3 px-6 pt-4">
            <input
                type="text"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search classifiers…"
                aria-label="Search classifiers"
                className={`w-full max-w-xs ${CONTROL}`}
            />
            <select
                aria-label="Filter by modality"
                value={modality ?? ''}
                onChange={(e) => onModalityChange((e.target.value || undefined) as Modality | undefined)}
                className={CONTROL}
            >
                <option value="">All modalities</option>
                {MODALITY_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </select>
            <select
                aria-label="Filter by valence"
                value={valence ?? ''}
                onChange={(e) => onValenceChange((e.target.value || undefined) as Valence | undefined)}
                className={CONTROL}
            >
                <option value="">All valences</option>
                {VALENCE_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </select>
            <select
                aria-label="Filter by team"
                value={teamId ?? ''}
                onChange={(e) => onTeamChange(e.target.value ? Number(e.target.value) : undefined)}
                className={`max-w-[14rem] ${CONTROL}`}
            >
                <option value="">All teams</option>
                {teams.map((t) => (
                    <option key={t.id} value={t.id}>
                        {t.name}
                    </option>
                ))}
            </select>
            <AccountFilter
                selectedAccountIds={accountIds}
                selectedTeamIds={teamId === undefined ? [] : [teamId]}
                onUpdateSelectedAccounts={(accounts) => onAccountIdsChange(accounts.map((a) => a.id))}
            />
            <select
                aria-label="Filter by status"
                value={status}
                onChange={(e) => onStatusChange(e.target.value as ClassifierStatusFilter)}
                className={CONTROL}
            >
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="all">All</option>
            </select>
        </div>
    )
}
